import React from 'react';
import { FaGoogle, FaWindows, FaFingerprint, FaDesktop, FaBell } from 'react-icons/fa';
import { useLandingSettings } from '../hooks/useLandingSettings';

const DownloadApp: React.FC = () => {
  const { settings } = useLandingSettings();
  return (
    <section id="download" className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            Soluciones MultiGym
          </h2>
          <p className="text-xl text-blue-200 max-w-2xl mx-auto">
            Lleva el control de tu gimnasio al siguiente nivel con nuestras aplicaciones especializadas para móvil y escritorio.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">

          {/* Mobile App Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
            <div className="mb-6 flex justify-between items-start">
              <div className="bg-blue-500/20 p-4 rounded-2xl">
                <FaGoogle className="text-4xl text-blue-300" />
              </div>
              <span className="bg-blue-500 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Para Socios</span>
            </div>

            <h3 className="text-2xl font-bold mb-4">MultiGym Mobile App</h3>
            <p className="text-blue-100 mb-8 min-h-[80px]">
              El gimnasio en tu celular. Visualiza tus rutinas personalizadas, lleva el control de tus pagos y accede fácilmente con tu código QR.
            </p>

            <ul className="space-y-4 mb-8">
              <li className="flex items-center text-blue-50">
                <FaBell className="mr-3 text-blue-300" /> Seguiamiento de rutinas y progreso
              </li>
              <li className="flex items-center text-blue-50">
                <svg className="w-4 h-4 mr-3 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> Estado de cuenta y próximos pagos
              </li>
              <li className="flex items-center text-blue-50">
                <svg className="w-4 h-4 mr-3 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg> Revisa tus clases, inscribite !!!
              </li>
            </ul>

            <a
              href={settings.googlePlayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white hover:bg-gray-100 text-blue-900 px-6 py-4 rounded-xl flex items-center justify-center transition-all duration-300 font-semibold w-full"
            >
              <FaGoogle className="mr-3 text-xl group-hover:scale-110 transition-transform" />
              Descargar en Google Play
            </a>
          </div>

          {/* Desktop App Card */}
          <div className="bg-gradient-to-br from-indigo-900/50 to-blue-900/50 backdrop-blur-lg rounded-3xl p-8 border border-indigo-400/30 hover:border-indigo-400/50 transition-all duration-300 relative overflow-hidden">
            {/* Highlight effect */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full mix-blend-overlay filter blur-2xl opacity-50"></div>

            <div className="mb-6 flex justify-between items-start relative z-10">
              <div className="bg-indigo-500/20 p-4 rounded-2xl">
                <FaWindows className="text-4xl text-indigo-300" />
              </div>
              <span className="bg-indigo-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-lg shadow-indigo-500/30">Nuevo Lanzamiento</span>
            </div>

            <h3 className="text-2xl font-bold mb-4 relative z-10">Control de Asistencia</h3>
            <p className="text-indigo-100 mb-8 min-h-[80px] relative z-10">
              Software de escritorio para tu recepción con lector de huellas DigitalPersona. Verifica el estado "Al Día" de forma 100% automática.
            </p>

            <ul className="space-y-4 mb-8 relative z-10">
              <li className="flex items-center text-indigo-50">
                <FaFingerprint className="mr-3 text-indigo-300" /> Ingreso biométrico seguro
              </li>
              <li className="flex items-center text-indigo-50">
                <svg className="w-4 h-4 mr-3 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Validación "Al Día" automática
              </li>
              <li className="flex items-center text-indigo-50">
                <FaDesktop className="mr-3 text-indigo-300" /> Panel de gestión de huellas
              </li>
            </ul>

            <a
              href={settings.windowsAppUrl || "/setup/Multigym_Control_Asistencia_Setup_v1.0.0.exe"}
              download
              className="group bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-4 rounded-xl flex items-center justify-center transition-all duration-300 font-semibold w-full relative z-10 shadow-lg shadow-indigo-500/25"
            >
              <FaWindows className="mr-3 text-xl group-hover:scale-110 transition-transform" />
              Descargar Instalador (Windows)
            </a>
          </div>

        </div>
      </div>
    </section>
  );
};

export default DownloadApp;
